import * as React from 'react';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import LoginClient from 'Clients/LoginClient';
import LoginForm from 'Common/Forms/LoginForm';
import LinearProgress from '@material-ui/core/LinearProgress';
import logo from 'Resources/Images/kansas-logo.jpg';
import Typography from '@material-ui/core/Typography';
import Button from 'Common/Button';
import { WithStyles, createStyles, Theme } from '@material-ui/core';
import { LoginRequest, LoginResponse } from 'NSwag/ServerClient/Client.nswag';
import { IJwt } from 'Store/index';
import IconButton from '@material-ui/core/IconButton';
import TransitEnterExit from '@material-ui/icons/TransitEnterexit'

/*
 * Define any CSS styles for our component
 */
const styles = (theme: Theme) => createStyles({
	container: {
		position: 'absolute',
		top: 0,
		right: 0,
		margin: theme.spacing(2),
		zIndex: 1100,
		backgroundColor: '#FFFFFFEC',
		borderRadius: 5,
		padding: theme.spacing(2),
        maxWidth: 600,
        border: '1px solid #ddd'
	},
	flexContainer: {
		display: 'flex',
		flexWrap: 'wrap',
		alignItems: 'center',
		textAlign: 'center'
	},
	image: {
		maxWidth: '100px',
		margin: theme.spacing(3)
	},
	buttonContainer: {
		width: '100%'
	},
	expandIconContainer: {
		display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
		padding: theme.spacing(1) / 2,
		'&:hover': {
			cursor: 'pointer'
		}
    },
    childContainer: {
        marginRight: '1em'
    },
	progressBarContainer: {
		height: '100%',
		width: '100%',
		backgroundColor: '#FFFFFF88',
		position: 'absolute',
		top: 0,
		left: 0,
		borderRadius: 5,
		zIndex: 10001,
		display: 'flex',
		alignItems: 'center'
    },
    progressBar: {
        margin: theme.spacing(2),
        borderRadius: 10,
        width: '100%',
		height: 15
    },
    progressBarColorBackground: {
        backgroundColor: '#00CF1F55'
    },
    progressBarColor: {
	    backgroundColor: '#00CF1FAA'
    }
});

interface IState {
    username: string;
    password: string;
    loading: boolean;
    error: string;
    expanded: boolean;
    [key: string]: any;
}

interface IProps {
    classes: any;
    jwt: IJwt;
    children: any;
    setJwt(jwt: string): void;
    clearJwt(): void;
    clearStore(): void;
}

/*
 * Define the actual component, its state if it has one
 * and its logic
 */
class LoginDialog extends React.Component<IProps, IState> {
    /**
     * @constructor 
     * @param props - the properties given to this component
     */
    constructor(props: IProps) {
        super(props);

        this.state = {
            username: "",
            password: "",
            loading: false,
            error: "",
            expanded: true
        };
    }

    componentDidUpdate(prevProps: IProps, prevState: IState) {
        let username = localStorage.getItem("username");
        if (this.state.username === "" && username != null) {
            this.setState({username: username});
        }
    }

    /**
     * Sends a login request to the API and handles the response.
     */
    sendLoginRequest = () => {
        const { username, password } = this.state;
        const { clearJwt } = this.props;
        const request = new LoginRequest;

        this.clearError();
        this.setState({ loading: true });

        request.username = username;
        request.password = password;
        LoginClient.post(request).then((response: LoginResponse | null) => {
            if (response && response.jwt) {
                this.setState({ loading: false, password: "" }, () => this.props.setJwt(response.jwt!));
                localStorage.setItem('username', request.username!);
            } else if (response) {
                if (response.hasError) {
                    let error = response.error || "";
                    this.setState(({ password: "", loading: false, error }) as any);
                }
            }
        }).catch((error: any) => {
            console.log(error);
            clearJwt();
            localStorage.removeItem('username');
            if (!error.status) {
                this.setState({ error: "Unable to reach the server.", loading: false });
            } else {
                this.setState({ error: "Login failed.", loading: false });
            }
        });
    }

    /**
     * Clears the errors in the state
     */
    clearError = () => this.setState({ error: "" });

    /**
     * Checks if the key pressed was "Enter" and
     * send the login request if it was.
     */
    handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === "Enter") {
            this.sendLoginRequest();
        }
    }

    /*
     * Handles a change to a top level state variable
     */
    handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ [event.target.name]: event.target.value } as any);
    }

    /**
     * Generates styling for the expand icon container dynamically
     * based on whether the dialog is expanded or not.
     */
    getExpandIconContainerStyle = () => {
	    const { expanded } = this.state;
	    return {
            position: expanded ? 'absolute' : 'relative'
	    };
    }

	/**
	 * Generates styling for the expand icon dynamicaly based on
	 * whether or not the dialog is expanded.
	 */
    getExpandIconStyle = () => {
        const { expanded } = this.state;
	    return {
            transform: `rotate(${expanded ? '180' : '0'}deg)`
	    };
    }

    logout = () => {
        const { clearJwt, clearStore } = this.props;
        clearJwt();
        clearStore();
        localStorage.clear();
    }

	/*
	 * Define how the component will render (html elements or other components)
	 */
	render() {
		
        const { classes, jwt, children } = this.props;
        const { username, password, expanded, error, loading } = this.state;

		return (
            <div className={classes.container}>
                <div
                    className={classes.expandIconContainer}
                >
                    <div className={classes.childContainer}>
                        {children}
                    </div>
                    <IconButton edge="start" className={classes.menuButton}
                        color="inherit" aria-label="menu"
                        onClick={() => this.setState({ expanded: !expanded })}
                        style={this.getExpandIconStyle()}
                    >
                        <TransitEnterExit />
                    </IconButton>
                </div>

                {
                    loading &&
                    <div className={classes.progressBarContainer}>
                        <LinearProgress
                            classes={{ root: classes.progressBar, colorPrimary: classes.progressBarColorBackground, barColorPrimary: classes.progressBarColor }}
                        />
					</div>
	            }

                {
                    expanded &&
                    <div>
                        {
                            !jwt &&
                            <LoginForm
                                classes={{ container: classes.flexContainer, image: classes.image, buttonContainer: classes.buttonContainer }}
                                username={username}
                                password={password}
                                onChange={this.handleChange}
                                onKeyPress={this.handleKeyPress}
                                clearError={this.clearError}
                                onLogin={this.sendLoginRequest}
                                error={error}
                            />
                        }
                        {
                            jwt &&
                            <div className={classes.flexContainer}>
                                <div>
		                            <img className={classes.image} src={logo} alt="Kansas Department of Transportation Logo" />
                                </div>
                                <div>
	                                <Typography component="h5" gutterBottom>
		                                User: {username}
	                                </Typography>
                                    <Button
                                        onClick={this.logout}
                                        variant="outlined"
                                    >
		                                Logout
	                                </Button>
								</div>
                            </div>
                        }
                    </div>
                }
            </div>
		);

	}
}

/*
 * Make our component the default export object and attach the styles defined
 */
export default withStyles(styles)(LoginDialog)